import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import { useNavigate, useParams } from "react-router-dom";
import useFetchObject from "../../../../api/useFetchObject";
import useUpdate from "../../../../api/useUpdate";
import { useTranslation } from "react-i18next";

function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

const EditPatientTreatment = () => {
  const { t } = useTranslation();

  // Localization fallback helper
  const tr = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const { id } = useParams();

  // State to prevent resetting patient selection after user changes it
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  // Fetch treatment data
  const { data: treatment, isLoading: loadingTreatment } = useFetchObject(
    ["dental-treatments", id],
    `dental-treatments`,
    id,
    token
  );

  // Patients state and pagination
  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [patientInputValue, setPatientInputValue] = useState("");
  const [hasMore, setHasMore] = useState(true);

  // Fetch patients list with pagination and search
  const { data: patientsData, isLoading: loadingPatients } = useFetchObjects(
    ["patients", page, searchTerm],
    `patients/?page=${page}&page_size=50&search=${searchTerm}`,
    token
  );

  // Fetch procedures and dentists
  const { data: procedures, isLoading: loadingProcedures } = useFetchObjects(
    ["dental-procedure"],
    "dental-procedure",
    token
  );

  const { data: dentists, isLoading: loadingDentists } = useFetchObjects(
    ["users"],
    "users/?position=1",
    token
  );

  // Form states
  const [treatmentData, setTreatmentData] = useState({
    treatment_date: "",
    dentist: "",
    patient: "",
    is_paid: false,
    total_cost: 0,
    discount: 0,
  });

  const [selectedDentist, setSelectedDentist] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [selectedDetails, setSelectedDetails] = useState([]);
  const [autocompleteInput, setAutocompleteInput] = useState("");
  const autocompleteRef = useRef(null);

  const { handleUpdate, loading } = useUpdate(`dental-treatments`, token, -1);

  // Debounced search input handler
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPage(1);
    }, 300),
    []
  );

  // Handle user typing or clearing input
  const handlePatientInputChange = (event, value, reason) => {
    if (reason === "input") {
      setPatientInputValue(value);
      debouncedSetSearchTerm(value);
    }
    if (reason === "clear") {
      setPatientInputValue("");
      setSelectedPatient(null);
      setTreatmentData((prev) => ({ ...prev, patient: "" }));
    }
  };

  // Handle selection or clearing selection of patient
  const handlePatientChange = (event, value, reason) => {
    if (reason === "selectOption") {
      setSelectedPatient(value);
      setTreatmentData((prev) => ({
        ...prev,
        patient: value?.id || "",
      }));
      setPatientInputValue(
        value ? `${value.firstname} ${value.lastname || ""}`.trim() : ""
      );
      // Make sure patient is in the list
      setPatients((prev) => {
        if (value && !prev.find((p) => p.id === value.id)) {
          return [value, ...prev];
        }
        return prev;
      });
    }
    if (reason === "clear") {
      setSelectedPatient(null);
      setTreatmentData((prev) => ({ ...prev, patient: "" }));
      setPatientInputValue("");
    }
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 20 &&
      hasMore &&
      !loadingPatients
    ) {
      setPage((p) => p + 1);
    }
  };

  // Append patients from fetch and keep selected patient in list
  useEffect(() => {
    if (!patientsData) return;

    setPatients((prev) => {
      const merged = [...prev, ...patientsData.results];
      const uniquePatients = Array.from(
        new Map(merged.map((p) => [p.id, p])).values()
      );

      if (
        selectedPatient &&
        !uniquePatients.find((p) => p.id === selectedPatient.id)
      ) {
        uniquePatients.unshift(selectedPatient);
      }

      return uniquePatients;
    });

    setHasMore(!!patientsData.next);
  }, [patientsData, selectedPatient]);

  // Initialize form state on treatment load — run only once
  useEffect(() => {
    if (
      treatment &&
      !loadingPatients &&
      !loadingDentists &&
      !loadingProcedures &&
      dentists &&
      procedures &&
      !isInitialLoadDone
    ) {
      // Build patient object from treatment data
      let patient = patients.find((p) => p.id === treatment.patient);

      if (!patient && treatment.patient && treatment.patient_details) {
        patient = {
          id: treatment.patient,
          firstname: treatment.patient_details.name
            ? treatment.patient_details.name.split(" ")[0]
            : treatment.patient_details.name,
          lastname: treatment.patient_details.name
            ? treatment.patient_details.name.split(" ").slice(1).join(" ")
            : "",
          ...treatment.patient_details,
        };
        setPatients((prev) => [
          patient,
          ...prev.filter((p) => p.id !== patient.id),
        ]);
      }

      setSelectedPatient(patient || null);
      setPatientInputValue(
        patient ? `${patient.firstname} ${patient.lastname}`.trim() : ""
      );

      // Dentist
      const dentist = dentists.find((d) => d.id === treatment.dentist);
      setSelectedDentist(dentist || null);

      // Set treatment form data
      setTreatmentData({
        treatment_date: treatment.treatment_date || "",
        dentist: treatment.dentist || "",
        patient: treatment.patient || "",
        is_paid: treatment.is_paid || false,
        total_cost: treatment.total_cost || 0,
        discount: treatment.discount || 0,
      });

      // Set treatment details
      const detailsWithProcedures =
        treatment.treatment_details?.map((detail) => {
          const procedure = procedures.find((p) => p.id === detail.procedure);
          return {
            id: detail.id,
            procedureName: procedure?.name || detail.procedure_name || "",
            procedure: procedure || {
              id: detail.procedure,
              name: detail.procedure_name,
            },
            cost: detail.cost || 0,
            tooth_number: detail.tooth_number || "",
            total_price: detail.cost || 0,
          };
        }) || [];

      setSelectedDetails(detailsWithProcedures);
      setExistingImages(treatment.images || []);

      setIsInitialLoadDone(true); // Prevent re-running effect and resetting selection
    }
  }, [
    treatment,
    patients,
    dentists,
    procedures,
    loadingPatients,
    loadingDentists,
    loadingProcedures,
    isInitialLoadDone,
  ]);

  const calculateTotalCost = () => {
    const subtotal = selectedDetails.reduce((sum, detail) => {
      const cost = parseFloat(detail.cost) || 0;
      const toothNum = parseFloat(detail.tooth_number) || 1;
      return sum + cost * toothNum;
    }, 0);

    const effectiveDiscount = Math.min(treatmentData.discount, subtotal);
    const total = subtotal - effectiveDiscount;

    setTreatmentData((prevData) => ({
      ...prevData,
      total_cost: total.toFixed(2),
    }));
  };

  const handleAddProcedure = (procedure) => {
    if (procedure) {
      setSelectedDetails((prevDetails) => [
        ...prevDetails,
        {
          procedureName: procedure.name,
          procedure,
          cost: procedure.cost || 0,
          tooth_number: "",
          total_price: procedure.cost || 0,
        },
      ]);
      setAutocompleteInput("");
    }
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = [...selectedDetails];
    updatedDetails.splice(index, 1);
    setSelectedDetails(updatedDetails);
  };

  const handleUpdateDetail = (index, field, value) => {
    const updatedDetails = [...selectedDetails];
    updatedDetails[index][field] = value;

    const toothNum = parseFloat(updatedDetails[index].tooth_number) || 1;
    const cost = parseFloat(updatedDetails[index].cost) || 0;
    updatedDetails[index].total_price = toothNum * cost;

    setSelectedDetails(updatedDetails);
  };

  const handleNewImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const updatedImages = [...newImages];
      updatedImages[index] = file;
      setNewImages(updatedImages);
    }
  };

  const addImageField = () => {
    setNewImages([...newImages, null]);
  };

  const removeNewImageField = (index) => {
    const updatedImages = [...newImages];
    updatedImages.splice(index, 1);
    setNewImages(updatedImages);
  };

  const removeExistingImage = (imageId) => {
    setExistingImages(existingImages.filter((img) => img.id !== imageId));
  };

  useEffect(() => {
    calculateTotalCost();
  }, [selectedDetails, treatmentData.discount]);

  const handleChangeField = (e) => {
    const { name, value, type, checked } = e.target;
    setTreatmentData({
      ...treatmentData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const validateImages = () => {
    let isValid = true;
    const errors = [];
    newImages.forEach((image, index) => {
      if (!image) {
        errors[index] = tr("call_center.imageRequired", "Image is required");
        isValid = false;
      } else if (image.size > 2 * 1024 * 1024) {
        errors[index] = tr(
          "call_center.imageTooLarge",
          "Image size must be less than 2MB"
        );
        isValid = false;
      } else if (!image.type.startsWith("image/")) {
        errors[index] = tr(
          "call_center.invalidImageType",
          "Invalid image type"
        );
        isValid = false;
      }
    });
    setImageErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!treatmentData.patient) {
      errors.patient = tr("dental.patientRequired", "Patient is required");
    }
    if (!treatmentData.dentist) {
      errors.dentist = tr("dental.dentistRequired", "Dentist is required");
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!validateImages()) {
      return;
    }

    const formData = new FormData();

    formData.append("treatment_date", treatmentData.treatment_date);
    formData.append("dentist", treatmentData.dentist);
    formData.append("patient", treatmentData.patient);
    formData.append("is_paid", treatmentData.is_paid);
    formData.append("total_cost", treatmentData.total_cost);
    formData.append("discount", treatmentData.discount);

    formData.append(
      "details",
      JSON.stringify(
        selectedDetails.map((detail) => ({
          id: detail.id || null,
          procedure: detail.procedure?.id || "",
          tooth_number: detail.tooth_number || "",
          cost: detail.cost || 0,
        }))
      )
    );

    formData.append(
      "existing_images",
      JSON.stringify(existingImages.map((img) => img.id))
    );

    newImages.forEach((image) => {
      if (image) {
        formData.append(`new_images`, image);
      }
    });

    handleUpdate(id, formData, () => {
      navigate("/dental/patient-treatments-list");
    });
  };

  if (loadingTreatment) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {tr("dental.editTreatmentTitle", "Edit Treatment")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {tr("dental.patientInfo", "Patient Information")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={tr("dental.treatmentDate", "Treatment Date")}
                name="treatment_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={treatmentData.treatment_date}
                onChange={handleChangeField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={patients}
                getOptionLabel={(option) =>
                  option ? `${option.firstname} ${option.lastname || ""}` : ""
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                value={selectedPatient}
                loading={loadingPatients}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tr("dental.patient", "Patient")}
                    placeholder={tr(
                      "dental.searchByIdOrName",
                      "Search by ID or Name"
                    )}
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPatients && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {loadingDentists ? (
                <CircularProgress size={24} />
              ) : (
                <Autocomplete
                  options={dentists || []}
                  getOptionLabel={(option) => `${option.username}`}
                  value={selectedDentist}
                  onChange={(event, value) => {
                    setSelectedDentist(value);
                    setTreatmentData({
                      ...treatmentData,
                      dentist: value?.id || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tr("dental.dentist", "Dentist")}
                      error={!!formErrors.dentist}
                      helperText={formErrors.dentist}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={treatmentData.is_paid}
                    onChange={handleChangeField}
                    name="is_paid"
                    color="primary"
                  />
                }
                label={tr("dental.paymentStatus", "Payment Status")}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {tr("dental.addProcedure", "Add Procedure")}
          </Typography>
          {loadingProcedures ? (
            <CircularProgress size={24} />
          ) : (
            <Autocomplete
              ref={autocompleteRef}
              value={null}
              inputValue={autocompleteInput}
              onInputChange={(event, newInputValue) =>
                setAutocompleteInput(newInputValue)
              }
              onChange={(event, value) => handleAddProcedure(value)}
              options={procedures || []}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tr("dental.searchProcedure", "Search Procedure")}
                  placeholder={tr(
                    "dental.searchProcedurePlaceholder",
                    "Search procedures"
                  )}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
              )}
            />
          )}
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>
                  {tr("dental.procedureName", "Procedure Name")}
                </TableCell>
                <TableCell align="center">
                  {tr("dental.toothNumber", "Tooth Number")}
                </TableCell>
                <TableCell align="center">
                  {tr("dental.procedureCost", "Procedure Cost")}
                </TableCell>
                <TableCell align="center">
                  {tr("dental.totalPrice", "Total Price")}
                </TableCell>
                <TableCell align="center">
                  {tr("dental.action", "Action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {selectedDetails.map((detail, index) => (
                <TableRow key={index} hover>
                  <TableCell>{detail.procedureName}</TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      placeholder={tr(
                        "dental.enterToothNumber",
                        "Enter Tooth Number"
                      )}
                      value={detail.tooth_number}
                      onChange={(e) =>
                        handleUpdateDetail(
                          index,
                          "tooth_number",
                          e.target.value
                        )
                      }
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      value={detail.cost}
                      onChange={(e) =>
                        handleUpdateDetail(index, "cost", e.target.value)
                      }
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {detail.total_price?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={tr("dental.removeProcedure", "Remove Procedure")}
                      arrow
                    >
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveDetail(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Images Section */}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              backgroundColor: "#417690",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            {tr("call_center.uploadImages", "Upload Images")}
          </Typography>

          {/* Existing Images */}
          {existingImages.map((image, index) => (
            <React.Fragment key={`existing-${image.id}`}>
              <Box
                sx={{
                  height: "40px",
                  width: "100%",
                  backgroundColor: "#F8F8F8",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  paddingLeft={1}
                >
                  {tr("call_center.existingImage", "Existing Image")} #
                  {index + 1}
                </Typography>
                <IconButton
                  onClick={() => removeExistingImage(image.id)}
                  sx={{ color: "red" }}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  minHeight: "70px",
                  width: "100%",
                  backgroundColor: "white",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 1,
                }}
              >
                <img
                  src={image.image}
                  alt={`Treatment ${index}`}
                  style={{ maxHeight: "100px", maxWidth: "150px" }}
                />
              </Box>
            </React.Fragment>
          ))}

          {/* New Images */}
          {newImages.map((image, index) => (
            <React.Fragment key={`new-${index}`}>
              <Box
                sx={{
                  height: "40px",
                  width: "100%",
                  backgroundColor: "#F8F8F8",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  paddingLeft={1}
                >
                  {tr("call_center.newImage", "New Image")} #{index + 1}
                </Typography>
                <IconButton
                  onClick={() => removeNewImageField(index)}
                  sx={{ color: "red" }}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  minHeight: "70px",
                  width: "100%",
                  backgroundColor: "white",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Grid container marginTop={1} marginBottom={1}>
                  <Grid item xl={3} lg={3} md={3} sm={3} xs={4}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      paddingLeft={1}
                      sx={{
                        fontSize: {
                          xl: "15px",
                          lg: "15px",
                          md: "14px",
                          sm: "12px",
                          xs: "10px",
                        },
                      }}
                    >
                      {tr("call_center.imageFile", "Image File")}:
                    </Typography>
                  </Grid>
                  <Grid item xl={9} lg={9} md={9} sm={9} xs={8}>
                    <Box
                      sx={{
                        border: "none",
                        outline: "none",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                        "&::-webkit-file-upload-button": {
                          cursor: "pointer",
                          borderRadius: "4px",
                          padding: "5px 10px",
                          marginRight: 1,
                          border: "none",
                        },
                      }}
                      component={"input"}
                      type="file"
                      onChange={(e) => handleNewImageChange(e, index)}
                      accept="image/*"
                    />
                    <Typography fontSize="10px" color="error">
                      {imageErrors[index]}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </React.Fragment>
          ))}

          <Box
            sx={{
              height: "40px",
              width: "100%",
              backgroundColor: "#F8F8F8",
              color: "black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Button
              onClick={addImageField}
              startIcon={<AddIcon sx={{ color: "green" }} />}
              size="small"
              sx={{
                color: "#477893",
                fontSize: "12px",
                textTransform: "none",
              }}
            >
              {tr("call_center.addImage", "Add Image")}
            </Button>
          </Box>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
          }}
        >
          <Box>
            <TextField
              fullWidth
              label={tr("dental.discount", "Discount")}
              name="discount"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">؋</InputAdornment>,
              }}
              value={treatmentData.discount}
              onChange={handleChangeField}
              sx={{ backgroundColor: "#fff", borderRadius: 2, width: "200px" }}
            />
          </Box>
          <Typography
            variant="h6"
            color="primary"
            sx={{
              fontWeight: { xs: 400, md: 600 },
              fontSize: { xs: "12px", md: "16px" },
            }}
          >
            {tr("dental.totalCost", "Total Cost")}: {treatmentData.total_cost} ؋
          </Typography>
          <Button
            size="medium"
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: { xs: 400, md: 700 },
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading
              ? tr("dental.updating", "Updating...")
              : tr("dental.updateTreatment", "Update Treatment")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EditPatientTreatment;
